import { LocalStorageKeys } from '@/constants/enums/LocalStorageEnum';
import { RouteEnum } from '@/constants/enums/RouteEnum';
import { UserPlanTypeEnum } from '@/constants/user';
import type { StorageSexValue, StorageUserStore } from '@/models/interface/storageValue';
import FDMessage from '@/plugins/fdMessage';
import { landingPageRoutes } from '@/router/routes/landingPage';
import { loginTypeRoutes } from '@/router/routes/loginType';
import { loginState, setLoginStateCookie, setToken, tokenExpire } from '@/utils/token';
import { UtilCommon } from '@/utils/utilCommon';
import { useCookies } from '@vueuse/integrations/useCookies';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

interface jwtDecodeType {
  exp: number;
  iss: string;
  member_id: string;
  nbf: string;
}

const cookies = useCookies(['fd_member_id']);

/**
 * 無需 JWT Token 驗證的頁面
 */
export const noTokenRequiredPages: string[] = [
  ...[...loginTypeRoutes, ...landingPageRoutes].map(
    (route: RouteRecordRaw) => route.name as string
  ),
  RouteEnum.ThankYou
];

/**
 * 紀錄會員 ID 至 cookie，供網站異常紀錄排查登入者名單使用
 * @param memberId - 會員 ID
 */
function setFdMemberIdCookie(memberId: string | number) {
  cookies.set('fd_member_id', memberId, {
    path: '/',
    domain: '.funday.asia',
    sameSite: 'none',
    secure: true,
    expires: dayjs().add(3, 'day').toDate()
  });
}

/**
 * fdtk 使用標準的 encodeURIComponent 函數進行編碼
 * @param str - 編碼的字串
 * @remarks 將編碼後的十六進位數字轉換為小寫
 */
function customEncodeURIComponent(str: string) {
  return encodeURIComponent(str).replace(/%[0-9A-F]{2}/g, (matched) => matched.toLowerCase());
}

/**
 * 取得學期制方案資訊
 * @param userStore - 使用者狀態管理 store
 */
const setAyceContractInfo = async (userStore: ReturnType<typeof useUserStore>): Promise<void> => {
  try {
    const content = await userApi.getAyceContract();
    userStore.ayceContractInfo = { ...content };
  } catch (errors: unknown) {
    FDMessage.error(`取得學期制方案資訊失敗，請稍後再試。`);
    throw new Error(`[Middleware Auth] 取得學期制方案資訊失敗: ${JSON.stringify(errors)}`);
  }
};

/**
 * 更新使用者資訊
 * @param token - JWT Token
 */
async function fetchUserInfo(
  token: string
): Promise<void | { name: string; replace: boolean } | false> {
  const userStore = useUserStore();
  const popStore = usePopStore();

  popStore.loadingStatus = true;

  try {
    const memberId = jwtDecode<jwtDecodeType>(token).member_id;

    // 取得 使用者資訊
    userStore.userData = await userApi.getMemberInfo(memberId);

    // 取得 定期制方案資訊 (定期制會員才發出請求)
    if (userStore.userData.planType === UserPlanTypeEnum.Ayce) {
      await setAyceContractInfo(userStore);
    }

    // 設定 fd_member_id cookie
    setFdMemberIdCookie(userStore.userData.memberId);

    // 取得 是否有專屬顧問 (電銷人員)
    userStore.hasSales = await userApi.hasSales();

    // 取得 是否為付費會員
    userStore.isPayUser = await userApi.isPayUser();

    // 取得 所有類型票券的剩餘次數 (僅限 V1 會員)
    if (!userStore.isV2OfficialUser) {
      userStore.remainingTickets = await userApi.remainingTickets();
    }

    if (!userStore.isPayUser) {
      // 取得 使用者手機號碼是否存在
      userStore.phoneExist = await userApi.getPhoneExist();

      if (!userStore.phoneExist) {
        if (userStore.isAfterThirdPartVerify) {
          return { name: RouteEnum.ThirdPartySMSVerify, replace: true };
        } else {
          userStore.logout();
          return false;
        }
      }

      // 取得 使用者是否已領取過上課券 (僅限 V1 會員)
      if (!userStore.isV2OfficialUser) {
        userStore.isWithdrawCoupon = await userApi.isWithdraw();
      }
    }

    userStore.isLogin = true;
    userStore.customerId = userStore.userData.customerId;
    userStore.aiTeacherAssistant = userStore.userData.chatIds.aiTeacherAssistant;

    const storageUser = UtilCommon.getLocalStorage<StorageUserStore>(LocalStorageKeys.UserStore);
    if (storageUser) {
      const storage = UtilCommon.getLocalStorage<StorageSexValue[]>('sex');
      const newStorage: StorageSexValue[] = [
        {
          userAccount: storageUser.userAccount ?? '',
          value: userStore.userData.sex
        }
      ];

      if (storage) {
        newStorage.push(
          ...storage.filter((item) => item.userAccount !== (storageUser.userAccount ?? ''))
        );
      }

      UtilCommon.setLocalStorage<StorageSexValue[]>('sex', newStorage);
    }
  } catch (errors: unknown) {
    console.error(`[Auth Middleware] 取得使用者資訊失敗或異常: ${JSON.stringify(errors)}`);
    return false;
  } finally {
    await UtilCommon.sleep(300);
    popStore.loadingStatus = false;
  }
}

export default async function auth({ to }: { to: RouteLocationNormalized }) {
  const user = useUserStore();
  const { isOpen, text, resolveAction, closeOnClickModal } = storeToRefs(useDialogStore());

  // 1. 免金鑰驗證頁面，直接放行
  if (noTokenRequiredPages.includes(to.name as string)) {
    return;
  }

  /**
   * 2. B2B 站或其他子站，定義登入用的金鑰驗證網址列參數
   *
   * @remarks fdtk 流程：
   * - 從子站登入後，前往 New Funday 可用頁面攜帶 fdtk 參數 (例如：https://www.funday.asia/home?fdtk=xxx)
   * - 路由守衛中檢查是否存在 fdtk 參數
   * - 執行登入驗證請求
   */
  if (to?.query?.fdtk) {
    // 驗證 fdtk 是否有效
    const fdtk = customEncodeURIComponent(to.query.fdtk as string);

    try {
      const { content: jwtToken } = await userApi.userLoginFdtk(fdtk);
      if (!jwtToken) {
        FDMessage({
          type: 'error',
          message: '取得 Token 失敗，請重新登入。',
          showClose: true,
          onClose: () => user.logout()
        });
        return false;
      }

      // 設置 Token 及登入狀態 (root domain cookie)
      setToken(jwtToken);
      setLoginStateCookie(true);
      user.setLoginState(jwtToken);

      const result = await fetchUserInfo(jwtToken);
      if (result === false) return false;

      // Token 驗證成功後，清除網址列中的 fdtk 參數，避免殘留在瀏覽器歷史紀錄
      const { fdtk: _fdtk, ...restQuery } = to.query;
      return { path: to.path, query: restQuery };
    } catch (errors: unknown) {
      FDMessage({
        type: 'error',
        message: `fdtk 金鑰驗證異常，請再重新登入。`,
        showClose: true,
        onClose: () => user.logout()
      });

      console.error(`[Auth Middleware] fdtk 金鑰驗證異常: ${JSON.stringify(errors)}`);
      return false;
    }
  }

  // 3. 未登入或 Token 不存在，直接登出
  const currentLoginState = loginState();
  if (!currentLoginState || !user.jwtTokenExist) {
    user.logout();
    return false;
  }

  // 4. 檢查 JWT Token 是否過期
  const expireToken = tokenExpire(user.token);

  if (expireToken) {
    text.value = {
      title: '登入逾時',
      content: user.forceLogoutMsg,
      actionBtn: '確定',
      contentNeedCenter: true
    };

    resolveAction.value = async () => user.logout();
    isOpen.value = true;
    closeOnClickModal.value = false;

    return false;
  }

  // 5. 已登入且有使用者資訊，直接放行
  if (user.hasUserInfo) {
    return;
  }

  // 6. 已登入但無使用者資訊，取得資訊後放行
  return fetchUserInfo(user.token);
}
